import {Form, Modal} from 'antd';
import {
  AccountFieldsFragment,
  ChurnReason,
  InputMaybe,
  UpdateAccountInput,
  useUpdateAccountMutation
} from 'api/generated';
import {Formik} from 'formik';
import {FormItem, Input, Select} from 'formik-antd';
import React from 'react';
import {
  churnReasonLabels,
  platformChurnReasonOptions,
  switchedChurnReasonOptions
} from '../../lib/labels';
import {nullify} from '../../lib/utils';

type Props = {
  account?: AccountFieldsFragment;
  isOpen: boolean;
  onClose: () => void;
};

const {TextArea} = Input;

export const ChurnedReasonForm: React.FC<Props> = ({account, isOpen, onClose}) => {
  const [updateAccount] = useUpdateAccountMutation();
  if (!account) return null;

  const initialValues: UpdateAccountInput = {
    id: account.id,
    churnReason: account.churnReason,
    churnReasonOption: account.churnReasonOption,
    churnNote: account.churnNote
  };

  const handleUpdateAccount = (input: UpdateAccountInput) => {
    if (!input.churnReason) {
      input.churnReason = null;
      input.churnNote = null;
      input.churnReasonOption = null;
    } else {
      if (![ChurnReason.PROBLEM_WITH_PLATFORM, ChurnReason.SWITCHED].includes(input.churnReason)) {
        input.churnReasonOption = null;
      }
      if (input.churnReason === ChurnReason.SWITCHED && input.churnReasonOption !== 'Other') {
        input.churnNote = null;
      }
      if (
        input.churnReason === ChurnReason.PROBLEM_WITH_PLATFORM &&
        input.churnReasonOption !== 'Other'
      ) {
        input.churnNote = null;
      }
      if (
        ![ChurnReason.OTHER, ChurnReason.SWITCHED, ChurnReason.PROBLEM_WITH_PLATFORM].includes(
          input.churnReason
        )
      ) {
        input.churnNote = null;
      }
      if (input.churnReasonOption === undefined) {
        input.churnReasonOption = null;
      }
    }
    return updateAccount({variables: {input: nullify(input)}}).then(onClose);
  };

  const hasChurnNote = (
    reason: InputMaybe<ChurnReason> | undefined,
    option: InputMaybe<string> | undefined
  ) => {
    return (
      reason === ChurnReason.OTHER ||
      (reason === ChurnReason.SWITCHED && option === 'Other') ||
      (reason === ChurnReason.PROBLEM_WITH_PLATFORM && option === 'Other')
    );
  };

  return (
    <>
      <Formik<UpdateAccountInput> initialValues={initialValues} onSubmit={handleUpdateAccount}>
        {({submitForm, isSubmitting, resetForm, values, setFieldValue}) => (
          <Modal
            title="Churn reason"
            okText="Save"
            open={isOpen}
            confirmLoading={isSubmitting}
            onOk={submitForm}
            onCancel={() => {
              onClose();
            }}>
            <Form layout="vertical">
              <FormItem name="churnReason" label="Churn reason">
                <Select
                  name="churnReason"
                  allowClear
                  options={Object.entries(churnReasonLabels).map(([value, label]) => ({
                    label,
                    value
                  }))}
                  onChange={(value) => {
                    if (
                      value &&
                      [ChurnReason.PROBLEM_WITH_PLATFORM, ChurnReason.SWITCHED].includes(value)
                    ) {
                      setFieldValue('churnReasonOption', null);
                      setFieldValue('churnNote', null);
                    }
                  }}
                />
              </FormItem>
              {values.churnReason === ChurnReason.SWITCHED && (
                <FormItem name="churnReasonOption" label="Churn reason option">
                  <Select
                    allowClear
                    name="churnReasonOption"
                    options={switchedChurnReasonOptions.map((option) => ({
                      label: option,
                      value: option
                    }))}
                  />
                </FormItem>
              )}{' '}
              {values.churnReason === ChurnReason.PROBLEM_WITH_PLATFORM && (
                <FormItem name="churnReasonOption" label="Churn reason option">
                  <Select
                    name="churnReasonOption"
                    allowClear
                    options={platformChurnReasonOptions.map((option) => ({
                      label: option,
                      value: option
                    }))}
                  />
                </FormItem>
              )}
              {hasChurnNote(values.churnReason, values.churnReasonOption) && (
                <FormItem name="churnNote" label="Churn note">
                  <TextArea name="churnNote" allowClear />
                </FormItem>
              )}
            </Form>
          </Modal>
        )}
      </Formik>
    </>
  );
};
